/* eslint-disable max-lines */

// Adapted from https://github.com/trailbehind/leaflet-grids/blob/master/mgrs.js

import {lineString, point} from '@turf/helpers';
import polygonize from '@turf/polygonize';
import flatten from 'lodash/flatten';
import range from 'lodash/range';
import {getLineIntersection, makeMeridian, makeParallel, makeSources} from './GridUtils';

export interface UTMPosition {
    northing: number;
    easting: number;
    zoneNumber: number;
    zoneLetter: string;
    accuracy?: number;
}

const eccSquared = 0.00669438;
const ellipsoidRadius = 6378137.0;
const eccPrimeSquared = eccSquared / (1 - eccSquared);
const e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared));
const SCALE_FACTOR = 0.9996;

// Assumes input is a polygon without rings
function centroid(feature: GeoJSON.Feature<GeoJSON.Polygon | null>) {
    let x = 0;
    let y = 0;

    if (!feature.geometry || !feature.geometry.coordinates) {
        return [];
    }
    feature.geometry.coordinates[0].forEach((coord: GeoJSON.Position) => {
        x += coord[0];
        y += coord[1];
    });
    return [x / feature.geometry.coordinates[0].length, y / feature.geometry.coordinates[0].length];
}

// Snap the start and end of a line to two bounding meridians
function cleanLine(line: GeoJSON.Position[], leftLng: number, rightLng: number) {
    const pt1 = line[0];
    const pt2 = line[line.length - 1];
    const slope = (pt1[1] - pt2[1]) / (pt1[0] - pt2[0]);
    const b = pt1[1] - slope * pt1[0];

    const newLeftLat = slope * leftLng + b;
    const newPt1 = [leftLng, newLeftLat];

    const newRightLat = slope * rightLng + b;
    const newPt2 = [rightLng, newRightLat];

    return [newPt1, newPt2];
}

export function degToRad(deg: number) {
    return deg * (Math.PI / 180.0);
}

export function drawMeridians(
    bounds: mapboxgl.LngLatBounds
): GeoJSON.Feature<GeoJSON.LineString>[] {
    const minLng = Math.max(snapFloor(bounds.getWest(), 6), -180);
    const maxLng = snapCeil(bounds.getEast(), 6);

    const minLat = Math.max(snapFloor(bounds.getSouth(), 8), -80);
    let maxLat = snapCeil(bounds.getNorth(), 8);

    if (maxLat >= 80) {
        maxLat = 84;
    }

    const lines = [];

    for (let lng = minLng; lng <= maxLng; lng += 6) {
        // Handle Norway
        if (lng === 6 && maxLat >= 56) {
            if (maxLat >= 56) {
                lines.push(
                    lineString(makeMeridian(3, 56, 64, 2), {
                        deg: 3,
                        type: 'zone',
                        zone: 32,
                    })
                );
            }

            if (minLat < 56) {
                lines.push(
                    lineString(makeMeridian(6, minLat, 56, 2), {
                        deg: 6,
                        type: 'zone',
                        zone: 32,
                    })
                );
            }

            if (maxLat >= 64) {
                lines.push(
                    lineString(makeMeridian(6, 64, 72, 2), {
                        deg: 6,
                        type: 'zone',
                        zone: 32,
                    })
                );
            }

            if (maxLat >= 72) {
                lines.push(
                    lineString(makeMeridian(9, 72, maxLat, 1), {
                        deg: 9,
                        type: 'zone',
                        zone: getUTMZone([9]),
                    })
                );
            }
        } else if (lng > 6 && lng < 42 && maxLat >= 72) {
            // Draw south of the special zone
            lines.push(
                lineString(makeMeridian(lng, minLat, 72, 1), {
                    deg: lng,
                    type: 'zone',
                    zone: getUTMZone([lng]),
                })
            );

            // Only draw it once
            if (getUTMZone([lng]) % 2 === 0) {
                lines.push(
                    lineString(makeMeridian(lng + 3, 72, maxLat, 1), {
                        deg: lng + 3,
                        type: 'zone',
                        zone: getUTMZone([lng]),
                    })
                );
            }
        } else {
            const zoneLine = lineString(makeMeridian(lng, minLat, maxLat, 2), {
                deg: lng,
                type: 'zone',
                zone: getUTMZone([lng]),
            });

            lines.push(zoneLine);
        }
    }
    return lines;
}

export function drawParallels(bounds: mapboxgl.LngLatBounds) {
    const minLng = Math.max(snapFloor(bounds.getWest(), 6), -180);
    const maxLng = snapCeil(bounds.getEast(), 6);

    const minLat = Math.max(snapFloor(bounds.getSouth(), 8), -80);
    let maxLat = snapCeil(bounds.getNorth(), 8);

    if (maxLat >= 80) {
        maxLat = 84;
    }

    const lines = [];
    for (let lat = minLat; lat <= maxLat; lat += 8) {
        if (lat === 80) {
            lines.push(
                lineString(makeParallel(84, minLng, maxLng, 1), {
                    deg: lat,
                    type: 'zone-parallel',
                })
            );
        } else {
            lines.push(
                lineString(makeParallel(lat, minLng, maxLng, 1), {
                    deg: lat,
                    type: 'zone-parallel',
                })
            );
        }
    }

    return lines;
}

function drawZoneMeridians(
    min: GeoJSON.Position,
    max: GeoJSON.Position,
    interval: number
): GeoJSON.Feature<GeoJSON.LineString>[] {
    const output = [];
    let minEasting = snapFloor(toUTM(min).easting, interval);
    const maxNorthing = toUTM(max).northing;
    const minNorthing = toUTM(min).northing;
    const maxEasting = toUTM(max).easting;

    // Center is used for getting the correct zone number and letter for generated utm coordinates
    const center = toUTM([min[0] + (max[0] - min[0]) / 2, min[1] + (max[1] - min[1]) / 2]);

    const rightEdge: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
            type: 'LineString',
            coordinates: [max, [max[0], min[1]]],
        },
    };
    const leftEdge: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
            type: 'LineString',
            coordinates: [min, [min[0], max[1]]],
        },
    };

    while (minEasting <= maxEasting + interval) {
        const topVertex = fromUTM({
            easting: minEasting,
            northing: maxNorthing,
            zoneLetter: center.zoneLetter,
            zoneNumber: center.zoneNumber,
        });
        const bottomVertex = fromUTM({
            easting: minEasting,
            northing: minNorthing,
            zoneLetter: center.zoneLetter,
            zoneNumber: center.zoneNumber,
        });

        const line: GeoJSON.Feature<GeoJSON.LineString> = {
            type: 'Feature',
            properties: {
                type: 'grid',
                label: minEasting,
            },
            geometry: {
                type: 'LineString',
                coordinates: [topVertex, bottomVertex],
            },
        };

        // Ignore the lines if they completely fall outside the zone
        if (
            line.geometry.coordinates[0][0] < leftEdge.geometry.coordinates[0][0] &&
            line.geometry.coordinates[1][0] < leftEdge.geometry.coordinates[1][0]
        ) {
            minEasting += interval;
            continue;
        }
        if (
            line.geometry.coordinates[0][0] > rightEdge.geometry.coordinates[0][0] &&
            line.geometry.coordinates[1][0] > rightEdge.geometry.coordinates[1][0]
        ) {
            minEasting += interval;
            continue;
        }

        // Cut the lines at their intersection with the sides of the zone
        const rightIntersection = getLineIntersection(line, rightEdge);
        if (rightIntersection.length === 2) {
            line.geometry.coordinates.splice(0, 1, rightIntersection);
        }

        const leftIntersection = getLineIntersection(line, leftEdge);
        if (leftIntersection.length === 2) {
            line.geometry.coordinates.splice(0, 1, leftIntersection);
        }

        output.push(line);
        minEasting += interval;
    }
    return output;
}

export function fromUTM(utm: UTMPosition) {
    if (utm.zoneNumber < 0 || utm.zoneNumber > 60) {
        return [];
    }
    // Check hemisphere and remove 10,000,000 meter offset used for southern hemisphere
    if (utm.zoneLetter < 'N') {
        utm.northing -= 10000000.0;
    }

    const M = utm.northing / SCALE_FACTOR;
    const mu =
        M /
        (ellipsoidRadius *
            (1 -
                eccSquared / 4 -
                (3 * eccSquared * eccSquared) / 64 -
                (5 * eccSquared * eccSquared * eccSquared) / 256));

    const phi1Rad =
        mu +
        ((3 * e1) / 2 - (27 * e1 * e1 * e1) / 32) * Math.sin(2 * mu) +
        ((21 * e1 * e1) / 16 - (55 * e1 * e1 * e1 * e1) / 32) * Math.sin(4 * mu) +
        ((151 * e1 * e1 * e1) / 96) * Math.sin(6 * mu);

    const N1 = ellipsoidRadius / Math.sqrt(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad));
    const T1 = Math.tan(phi1Rad) * Math.tan(phi1Rad);
    const C1 = eccPrimeSquared * Math.cos(phi1Rad) * Math.cos(phi1Rad);
    const R1 =
        (ellipsoidRadius * (1 - eccSquared)) /
        Math.pow(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad), 1.5);
    const D = (utm.easting - 500000.0) / (N1 * SCALE_FACTOR);

    let lat =
        phi1Rad -
        ((N1 * Math.tan(phi1Rad)) / R1) *
            ((D * D) / 2 -
                ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * eccPrimeSquared) * D * D * D * D) / 24 +
                ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * eccPrimeSquared - 3 * C1 * C1) *
                    D *
                    D *
                    D *
                    D *
                    D *
                    D) /
                    720);
    lat = radToDeg(lat);

    const lngOrigin = (utm.zoneNumber - 1) * 6 - 180 + 3; // +3 puts origin

    let lng =
        (D -
            ((1 + 2 * T1 + C1) * D * D * D) / 6 +
            ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * eccPrimeSquared + 24 * T1 * T1) *
                D *
                D *
                D *
                D *
                D) /
                120) /
        Math.cos(phi1Rad);
    lng = lngOrigin + radToDeg(lng);

    return [lng, lat];
}

// Calculates the MGRS letter designator for the given latitude
export function getBandLetter(lat: number) {
    if (lat <= 84 && lat >= 72) {
        return 'X';
    } else if (lat < 72 && lat >= 64) {
        return 'W';
    } else if (lat < 64 && lat >= 56) {
        return 'V';
    } else if (lat < 56 && lat >= 48) {
        return 'U';
    } else if (lat < 48 && lat >= 40) {
        return 'T';
    } else if (lat < 40 && lat >= 32) {
        return 'S';
    } else if (lat < 32 && lat >= 24) {
        return 'R';
    } else if (lat < 24 && lat >= 16) {
        return 'Q';
    } else if (lat < 16 && lat >= 8) {
        return 'P';
    } else if (lat < 8 && lat >= 0) {
        return 'N';
    } else if (lat < 0 && lat >= -8) {
        return 'M';
    } else if (lat < -8 && lat >= -16) {
        return 'L';
    } else if (lat < -16 && lat >= -24) {
        return 'K';
    } else if (lat < -24 && lat >= -32) {
        return 'J';
    } else if (lat < -32 && lat >= -40) {
        return 'H';
    } else if (lat < -40 && lat >= -48) {
        return 'G';
    } else if (lat < -48 && lat >= -56) {
        return 'F';
    } else if (lat < -56 && lat >= -64) {
        return 'E';
    } else if (lat < -64 && lat >= -72) {
        return 'D';
    } else if (lat < -72 && lat >= -80) {
        return 'C';
    }
    return 'Z';
}

export function getUTMZone(coord: GeoJSON.Position) {
    let zone = Math.floor((coord[0] + 180) / 6) + 1;

    if (coord.length === 1) {
        return zone;
    }
    // Make sure the longitude 180.00 is in Zone 60
    if (coord[0] === 180) {
        zone = 60;
    }

    // Special zone for Norway
    if (coord[1] >= 56.0 && coord[1] < 64.0 && coord[0] >= 3.0 && coord[0] < 12.0) {
        zone = 32;
    }

    // Special zones for Svalbard
    if (coord[1] >= 72.0 && coord[1] < 84.0) {
        if (coord[0] >= 0.0 && coord[0] < 9.0) {
            zone = 31;
        } else if (coord[0] >= 9.0 && coord[0] < 21.0) {
            zone = 33;
        } else if (coord[0] >= 21.0 && coord[0] < 33.0) {
            zone = 35;
        } else if (coord[0] >= 33.0 && coord[0] < 42.0) {
            zone = 37;
        }
    }

    return zone;
}

export function makeGridMeridians(bounds: mapboxgl.LngLatBounds, interval: number) {
    // Draw the lines a bit longer than we need to to assist with labeling
    const buffer = interval * 0.000001;
    const minLng = bounds.getWest() - buffer;
    const maxLng = bounds.getEast() + buffer;

    const minLat = bounds.getSouth() - buffer;
    const maxLat = bounds.getNorth() + buffer;

    // Get unique UTM zones that this bounding box intersects
    const zoneRange = Array.from(
        new Set([getUTMZone([minLng, minLat]), getUTMZone([maxLng, maxLat])]).values()
    );
    const zones = range(Math.min(...zoneRange), Math.max(...zoneRange) + 1, 1);

    const zoneMeridians = zones.map((z: number) => {
        const zoneMinLng = 6 * (z - 1) - 180;
        const zoneMaxLng = 6 * z - 180;

        const min = [Math.max(zoneMinLng, minLng) + 0.0001, minLat];
        const max = [Math.min(zoneMaxLng, maxLng) - 0.0001, maxLat];

        return drawZoneMeridians(min, max, interval);
    });

    return flatten(zoneMeridians);
}

export function makeGridParallels(
    bounds: mapboxgl.LngLatBounds,
    features: GeoJSON.Feature<GeoJSON.LineString>[],
    interval: number
): GeoJSON.Feature<GeoJSON.LineString, GeoJSON.GeoJsonProperties>[] {
    const buffer = 0.1;
    const minLng = bounds.getWest() - buffer;
    const maxLng = bounds.getEast() + buffer;

    const minLat = bounds.getSouth() - buffer;
    const maxLat = bounds.getNorth() + buffer;

    const boundsMaxNorthing = snapCeil(toUTM([minLng, maxLat]).northing, interval);
    const boundsMinNorthing = snapFloor(toUTM([minLng, minLat]).northing, interval);

    // const boundsMinEasting = snapFloor(toUTM([minLng, minLat]).easting, interval);
    // const boundsMaxEasting = snapCeil(toUTM([maxLng, maxLat]).easting, interval);

    const polygons = polygonize({type: 'FeatureCollection', features});
    const output = [];
    const fFactor = 0.000001;

    for (let i = 0; i < polygons.features.length; i++) {
        const lats = polygons.features[i].geometry!.coordinates[0].map((coord) => coord[1]);
        const maxPolyLat = Math.max(...lats);
        const minPolyLat = Math.min(...lats);

        const northLat = Math.min(maxPolyLat, maxLat);
        const southLat = Math.max(minPolyLat, minLat);

        const lngs = polygons.features[i].geometry!.coordinates[0].map((coord) => coord[0]);
        const minPolyLng = Math.min(...lngs);
        const maxPolyLng = Math.max(...lngs);

        const westLng = Math.max(minPolyLng, minLng) + fFactor;
        const eastLng = Math.min(maxPolyLng, maxLng) - fFactor;

        const min = toUTM([westLng, southLat]);
        const max = toUTM([eastLng, northLat]);

        const minNorthing = Math.min(boundsMinNorthing, snapCeil(min.northing, interval));
        const maxNorthing = Math.min(boundsMaxNorthing, snapFloor(max.northing, interval));

        let workingInterval = interval;

        while (minNorthing + workingInterval <= maxNorthing) {
            const left = fromUTM({
                ...min,
                northing: minNorthing + workingInterval,
            });
            const right = fromUTM({
                ...max,
                northing: minNorthing + workingInterval,
            });

            const line = [left, right];

            const cleaned = cleanLine(line, westLng, eastLng);

            if (line.length > 1) {
                const geometry: GeoJSON.LineString = {
                    type: 'LineString',
                    coordinates: cleaned,
                };

                const f: GeoJSON.Feature<GeoJSON.LineString, GeoJSON.GeoJsonProperties> = {
                    type: 'Feature',
                    properties: {
                        type: 'grid',
                        label: minNorthing + workingInterval,
                    },
                    geometry,
                };

                output.push(f);
            }

            workingInterval += interval;
        }
    }

    return output;
}

function makeGridLabels(
    bounds: mapboxgl.LngLatBounds,
    features: GeoJSON.Feature<GeoJSON.LineString>[]
) {
    // First make labels for the zones
    const output: GeoJSON.Feature<GeoJSON.Point>[] = [];
    const north = lineString([
        [bounds.getWest(), bounds.getNorth()],
        [bounds.getEast(), bounds.getNorth()],
    ]);
    const south = lineString([
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getEast(), bounds.getSouth()],
    ]);
    const west = lineString([
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getWest(), bounds.getNorth()],
    ]);
    const east = lineString([
        [bounds.getEast(), bounds.getNorth()],
        [bounds.getEast(), bounds.getSouth()],
    ]);

    features
        .filter((f) => f.properties!.type === 'zone')
        .forEach((f) => {
            const northIntersection = getLineIntersection(f, north);
            const southIntersection = getLineIntersection(f, south);

            if (northIntersection.length === 2) {
                output.push(
                    point(northIntersection, {
                        label: `Zone ${f.properties!.zone}`,
                        direction: 'north',
                        type: 'zone_grid_label',
                    })
                );
            }
            if (southIntersection.length === 2) {
                output.push(
                    point(southIntersection, {
                        label: `Zone ${f.properties!.zone}`,
                        direction: 'south',
                        type: 'zone_grid_label',
                    })
                );
            }
        });

    features
        .filter((f) => f.properties!.type === 'grid')
        .forEach((f) => {
            const northIntersection = getLineIntersection(f, north);
            const southIntersection = getLineIntersection(f, south);
            const westIntersection = getLineIntersection(f, west);
            const eastIntersection = getLineIntersection(f, east);

            if (northIntersection.length === 2) {
                output.push(
                    point(northIntersection, {
                        ...f.properties,
                        direction: 'north',
                        type: 'grid_label',
                    })
                );
            }
            if (southIntersection.length === 2) {
                output.push(
                    point(southIntersection, {
                        ...f.properties,
                        direction: 'south',
                        type: 'grid_label',
                    })
                );
            }
            if (westIntersection.length === 2) {
                output.push(
                    point(westIntersection, {
                        ...f.properties,
                        direction: 'west',
                        type: 'grid_label',
                    })
                );
            }
            if (eastIntersection.length === 2) {
                output.push(
                    point(eastIntersection, {
                        ...f.properties,
                        direction: 'east',
                        type: 'grid_label',
                    })
                );
            }
        });

    return output;
}

function makeZoneLabels(features: GeoJSON.Feature<GeoJSON.LineString>[]) {
    const polygons = polygonize({type: 'FeatureCollection', features});
    return polygons.features.map((feature) => {
        const centerCoord = centroid(feature);
        const center: GeoJSON.Feature<GeoJSON.Point> = {
            type: 'Feature',
            properties: {
                type: 'zone-label',
                label: `${getUTMZone([centerCoord[0]])}${getBandLetter(centerCoord[1])}`,
            },
            geometry: {
                type: 'Point',
                coordinates: centerCoord,
            },
        };

        return center;
    });
}

function radToDeg(rad: number) {
    return 180.0 * (rad / Math.PI);
}

export function snapFloor(coord: number, snap: number) {
    return Math.floor(coord / snap) * snap;
}
export function snapCeil(coord: number, snap: number) {
    return Math.ceil(coord / snap) * snap;
}

export function toUTM(coord: GeoJSON.Position): UTMPosition {
    const latRadians = degToRad(coord[1]);
    const lngRadians = degToRad(coord[0]);

    const zoneNumber = getUTMZone(coord);
    const lngOrigin = (zoneNumber - 1) * 6 - 180 + 3; //+3 puts origin
    const lngOriginRad = degToRad(lngOrigin);

    const N =
        ellipsoidRadius / Math.sqrt(1 - eccSquared * Math.sin(latRadians) * Math.sin(latRadians));
    const T = Math.tan(latRadians) * Math.tan(latRadians);
    const C = eccPrimeSquared * Math.cos(latRadians) * Math.cos(latRadians);
    const A1 = Math.cos(latRadians) * (lngRadians - lngOriginRad);
    const M =
        ellipsoidRadius *
        ((1 -
            eccSquared / 4 -
            (3 * eccSquared * eccSquared) / 64 -
            (5 * eccSquared * eccSquared * eccSquared) / 256) *
            latRadians -
            ((3 * eccSquared) / 8 +
                (3 * eccSquared * eccSquared) / 32 +
                (45 * eccSquared * eccSquared * eccSquared) / 1024) *
                Math.sin(2 * latRadians) +
            ((15 * eccSquared * eccSquared) / 256 +
                (45 * eccSquared * eccSquared * eccSquared) / 1024) *
                Math.sin(4 * latRadians) -
            ((35 * eccSquared * eccSquared * eccSquared) / 3072) * Math.sin(6 * latRadians));

    const easting =
        SCALE_FACTOR *
            N *
            (A1 +
                ((1 - T + C) * A1 * A1 * A1) / 6.0 +
                ((5 - 18 * T + T * T + 72 * C - 58 * eccPrimeSquared) * A1 * A1 * A1 * A1 * A1) /
                    120.0) +
        500000.0;

    let northing =
        SCALE_FACTOR *
        (M +
            N *
                Math.tan(latRadians) *
                ((A1 * A1) / 2 +
                    ((5 - T + 9 * C + 4 * C * C) * A1 * A1 * A1 * A1) / 24.0 +
                    ((61 - 58 * T + T * T + 600 * C - 330 * eccPrimeSquared) *
                        A1 *
                        A1 *
                        A1 *
                        A1 *
                        A1 *
                        A1) /
                        720.0));

    // 10000000 meter offset for southern hemisphere
    if (coord[1] < 0) {
        northing += 10000000.0;
    }

    return {
        northing: Math.trunc(northing),
        easting: Math.trunc(easting),
        zoneNumber: zoneNumber,
        zoneLetter: getBandLetter(coord[1]),
    };
}

export function update(
    bounds: mapboxgl.LngLatBounds,
    zoom: number,
    coordinateGridSpacing: number[]
) {
    zoom = Math.floor(zoom);
    const interval = coordinateGridSpacing[zoom] || coordinateGridSpacing[18];

    const features = [...drawMeridians(bounds), ...drawParallels(bounds)];
    const labels = makeZoneLabels(features);

    const gridParallels = zoom >= 6 ? makeGridParallels(bounds, features, interval) : [];
    const gridMeridians = zoom >= 6 ? makeGridMeridians(bounds, interval) : [];
    const gridLabels =
        zoom >= 6 ? makeGridLabels(bounds, [...features, ...gridParallels, ...gridMeridians]) : [];

    return {
        version: 8,
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
        metadata: {
            id: 'utm',
        },
        sources: makeSources([
            ...features,
            ...labels,
            ...gridMeridians,
            ...gridParallels,
            ...gridLabels,
        ]),
        layers: makeUTMLayers(),
    };
}

export function makeUTMLayers(): (mapboxgl.LineLayer | mapboxgl.SymbolLayer)[] {
    return [
        {
            id: 'graticule_line',
            type: 'line',
            source: 'graticule',
            filter: ['==', ['get', 'type'], 'zone'],
            paint: {
                'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1, 12, 2],
                'line-color': '#3C8CC8',
            },
        },
        {
            id: 'zone_parallel',
            type: 'line',
            source: 'graticule',
            filter: ['==', ['get', 'type'], 'zone-parallel'],
            paint: {
                'line-width': 1,
                'line-color': '#3C8CC8',
                'line-opacity': ['step', ['zoom'], 1, 6, 0],
            },
        },
        {
            id: 'utm_subdivision',
            type: 'line',
            source: 'graticule',
            filter: ['==', ['get', 'type'], 'grid'],
            paint: {
                'line-width': 1,
                'line-color': '#3C8CC8',
                'line-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 12, 1],
            },
        },
        {
            id: 'zone_label',
            type: 'symbol',
            source: 'graticule',
            filter: ['==', '$type', 'Point'],
            layout: {
                'text-field': ['get', 'label'],
                'text-anchor': 'center',
                'text-size': ['interpolate', ['linear'], ['zoom'], 0, 8, 6, 16],
                'text-font': ['Regular'],
            },
            paint: {
                'text-color': '#0C73AC',
                'text-opacity': ['step', ['zoom'], 1, 6, 0],
            },
        },
        {
            id: 'graticule_label',
            type: 'symbol',
            source: 'graticule',
            filter: ['==', ['get', 'type'], 'grid_label'],
            layout: {
                'text-field': ['get', 'label'],
                'text-justify': 'center',
                'text-anchor': [
                    'case',
                    ['==', ['get', 'direction'], 'north'],
                    'top',
                    ['==', ['get', 'direction'], 'south'],
                    'bottom',
                    ['==', ['get', 'direction'], 'west'],
                    'left',
                    ['==', ['get', 'direction'], 'east'],
                    'right',
                    'center',
                ],
                'text-size': ['interpolate', ['linear'], ['zoom'], 6, 10, 14, 14],
                'text-offset': [
                    'case',
                    ['==', ['get', 'direction'], 'north'],
                    ['literal', [0, 0.35]],
                    ['==', ['get', 'direction'], 'south'],
                    ['literal', [0, -0.1]],
                    ['==', ['get', 'direction'], 'west'],
                    ['literal', [0.5, 0]],
                    ['==', ['get', 'direction'], 'east'],
                    ['literal', [-0.5, 0]],
                    ['literal', [0, 0]],
                ],
                'text-font': ['Regular'],
            },
            paint: {
                'text-color': '#0C73AC',
            },
        },
        {
            id: 'zone_grid_label',
            type: 'symbol',
            source: 'graticule',
            filter: ['==', ['get', 'type'], 'zone_grid_label'],
            layout: {
                'text-field': ['get', 'label'],
                'text-justify': 'center',
                'text-anchor': [
                    'case',
                    ['==', ['get', 'direction'], 'north'],
                    'top-left',
                    ['==', ['get', 'direction'], 'south'],
                    'bottom-left',
                    ['==', ['get', 'direction'], 'west'],
                    'left',
                    ['==', ['get', 'direction'], 'east'],
                    'right',
                    'center',
                ],
                'text-size': ['interpolate', ['linear'], ['zoom'], 6, 12, 14, 16],
                'text-offset': [
                    'case',
                    ['==', ['get', 'direction'], 'north'],
                    ['literal', [0, 0.35]],
                    ['==', ['get', 'direction'], 'south'],
                    ['literal', [0, -0.1]],
                    ['==', ['get', 'direction'], 'west'],
                    ['literal', [0.5, 0]],
                    ['==', ['get', 'direction'], 'east'],
                    ['literal', [-0.5, 0]],
                    ['literal', [0, 0]],
                ],
                'text-font': ['Regular'],
            },
            paint: {
                'text-color': '#0C73AC',
            },
        },
    ];
}
